import { stringify } from 'yaml';

import type {
  EvictorAdvancedConfig,
  EvictorAdvancedEvictionConfigEntry,
  EvictorLabelSelector,
  EvictorLabelSelectorExpression,
} from '@cast/types-workload-eviction';

import {
  KEY_AND_VALUES_REQUIRED_ERROR_MESSAGE,
  KEY_REQUIRED_ERROR_MESSAGE,
  LABEL_VALUES_REQUIRED_ERROR_MESSAGE,
  MIN_REPLICAS,
  MIN_REPLICAS_ERROR_MESSAGE,
  RULE_PRESET_BY_TYPE,
  RULE_TYPE_REQUIRED_ERROR_MESSAGE,
  SELECTOR_CRITERIA_REQUIRED_ERROR_MESSAGE,
} from '../_constants';
import type {
  BuilderRule,
  LabelRowError,
  RuleType,
  RuleValidationResult,
} from '../_types';
import { notReachable } from '@cast/utils';

import {
  entryHasSelectorCriteria,
  isLabelKeyPresent,
  isLabelValuesComplete,
  operatorRequiresValues,
} from './advancedConfig';

export const isNodeRuleType = (ruleType: RuleType): boolean => {
  switch (ruleType) {
    case 'evict-node':
    case 'protect-node':
    case 'aggressive-node':
      return true;
    case 'evict-pod':
    case 'protect-pod':
    case 'aggressive-pod':
      return false;
    default:
      return notReachable(ruleType);
  }
};

export const isNodeRule = (rule: BuilderRule): boolean => {
  if (!rule.ruleType) {
    return false;
  }
  return isNodeRuleType(rule.ruleType);
};

const serializeLabelExpressions = (
  labels: EvictorLabelSelectorExpression[]
): EvictorLabelSelectorExpression[] => {
  return labels.map(({ key, operator, values }) => {
    const expression: EvictorLabelSelectorExpression = { key, operator };

    if (operatorRequiresValues(operator)) {
      expression.values = values;
    }

    return expression;
  });
};

const buildLabelSelector = (
  labels: EvictorLabelSelectorExpression[]
): EvictorLabelSelector => {
  return {
    matchExpressions: serializeLabelExpressions(labels),
  };
};

const mapBuilderRuleToEvictionConfigEntry = (
  rule: BuilderRule
): EvictorAdvancedEvictionConfigEntry => {
  if (!rule.ruleType) {
    return { settings: {} };
  }

  const { settingKey, enabled } = RULE_PRESET_BY_TYPE[rule.ruleType];
  const labelSelector = buildLabelSelector(rule.labels || []);

  const evictionConfigEntry: EvictorAdvancedEvictionConfigEntry = {
    settings: {
      [settingKey]: { enabled },
    },
    ...(isNodeRule(rule)
      ? { nodeSelector: { labelSelector } }
      : {
          podSelector: {
            labelSelector,
            namespace: rule.namespace,
            kind: rule.kind,
            replicasMin: rule.replicasMin,
          },
        }),
  };

  return evictionConfigEntry;
};

export const mapBuilderRulesToEvictionConfigEntries = (
  rules: BuilderRule[]
): EvictorAdvancedEvictionConfigEntry[] => {
  return rules.map(mapBuilderRuleToEvictionConfigEntry);
};

/**
 * Serializes the Builder rules back into form's evictionConfig YAML string
 * Note: labels are always emitted as matchExpressions, even if the source used matchLabels.
 */
export const mapBuilderRulesToYamlString = (rules: BuilderRule[]): string => {
  const evictionConfigEntries = mapBuilderRulesToEvictionConfigEntries(rules);

  const evictionConfig: EvictorAdvancedConfig = {
    evictionConfig: evictionConfigEntries,
  };

  return stringify(evictionConfig);
};

export const hasAnyCriteria = (rule: BuilderRule): boolean =>
  entryHasSelectorCriteria(mapBuilderRuleToEvictionConfigEntry(rule));

export const validateRule = (rule: BuilderRule): RuleValidationResult => {
  const labelErrors: LabelRowError[] = [];

  rule.labels?.forEach((label, index) => {
    if (!isLabelKeyPresent(label) && !isLabelValuesComplete(label)) {
      labelErrors.push({
        index,
        message: KEY_AND_VALUES_REQUIRED_ERROR_MESSAGE,
      });
      return;
    }
    if (!isLabelKeyPresent(label)) {
      labelErrors.push({
        index,
        message: KEY_REQUIRED_ERROR_MESSAGE,
      });
      return;
    }

    if (!isLabelValuesComplete(label)) {
      labelErrors.push({
        index,
        message: LABEL_VALUES_REQUIRED_ERROR_MESSAGE,
      });
    }
  });

  const hasLabelErrors = labelErrors.length > 0;
  const hasLabels = !!rule.labels?.length;

  if (isNodeRule(rule)) {
    return {
      valid: hasLabels && !hasLabelErrors,
      ruleError: hasLabels ? undefined : 'At least one label is required',
      labelErrors,
    };
  }

  const selectorCriteriaError = !hasAnyCriteria(rule)
    ? SELECTOR_CRITERIA_REQUIRED_ERROR_MESSAGE
    : undefined;

  const ruleError = rule.ruleType
    ? undefined
    : RULE_TYPE_REQUIRED_ERROR_MESSAGE;
  const replicasMinError =
    rule.replicasMin !== undefined && rule.replicasMin < MIN_REPLICAS
      ? MIN_REPLICAS_ERROR_MESSAGE
      : undefined;

  if (
    ruleError ||
    selectorCriteriaError ||
    replicasMinError ||
    hasLabelErrors
  ) {
    return {
      valid: false,
      ruleError: ruleError || selectorCriteriaError || replicasMinError,
      labelErrors,
    };
  }

  return { valid: true };
};
